import React, { useState } from 'react';
import { Modal, PrimaryBtn } from '../finance/shared';
import CategoryResolutionSection from './CategoryResolutionSection';
import { SUB_STATUS_LABELS } from './subscriptionUtils';

const METHODS = { cash: 'كاش', network: 'شبكة', transfer: 'تحويل', electronic: 'إلكتروني', other: 'أخرى' };
const COL_LABELS = {
  name: 'اسم اللاعب', status: 'الحالة', reg_date: 'تاريخ التسجيل', period: 'فترة الاستحقاق', amount: 'المبلغ',
  method: 'طريقة السداد', start: 'بداية الاشتراك', end: 'نهاية الاشتراك', pay_date: 'تاريخ السداد',
  discount: 'الخصم', discount_reason: 'سبب الخصم', arrears: 'المتأخرات', notes: 'ملاحظات',
};
const nf = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US'));

const SummaryCard = ({ value, label, tone, testId }) => (
  <div className={`rounded-xl p-2.5 text-center ${tone}`} data-testid={testId}>
    <div className="text-lg font-extrabold tabular-nums">{value}</div>
    <div className="text-[11px] font-semibold opacity-80">{label}</div>
  </div>
);

const SheetBlock = ({ sheet, excluded, onToggle }) => {
  const [open, setOpen] = useState(false);
  if (!sheet.detected) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid={`academy-sheet-skipped-${sheet.sheet}`}>
        <b>{sheet.sheet}</b> — تم تجاهل هذه الورقة: {sheet.reason}
      </div>
    );
  }
  const withPayment = sheet.players.filter((p) => p.amount && p.payment_date).length;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200" data-testid={`academy-sheet-${sheet.sheet}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 bg-slate-50 px-3 py-2.5 text-right transition-colors hover:bg-slate-100" data-testid={`academy-sheet-toggle-${sheet.sheet}`}>
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-slate-800">{sheet.sheet}</div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            {sheet.players.length} لاعب · {withPayment} دفعة · الفئة: {sheet.matched_category_id ? 'مربوطة بفئة موجودة' : 'ستُنشأ باسم الورقة'}
          </div>
        </div>
        <span className="shrink-0 text-xs font-bold text-teal-700">{open ? 'إخفاء' : 'عرض الصفوف'}</span>
      </button>

      {open && (
        <div className="space-y-2 p-3">
          <div className="flex flex-wrap gap-1">
            {Object.entries(sheet.mapped_columns || {}).map(([k, v]) => (
              <span key={k} className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700" title={`العمود في الملف: ${v}`}>
                {COL_LABELS[k] || k}
              </span>
            ))}
          </div>
          {(sheet.notes || []).map((n, i) => (
            <p key={i} className="rounded-lg bg-blue-50 px-2 py-1.5 text-[11px] text-blue-700">{n}</p>
          ))}
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-slate-500">
                <tr>
                  <th className="p-2 text-right">اللاعب</th>
                  <th className="p-2 text-right">الحالة</th>
                  <th className="p-2 text-right">المبلغ</th>
                  <th className="p-2 text-right">السداد</th>
                  <th className="p-2 text-right">الاشتراك</th>
                  <th className="p-2 text-right">المتبقي / التأخير</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {sheet.players.map((p) => {
                  const off = excluded.has(p.key);
                  return (
                    <tr key={p.key} className={`border-t border-slate-100 ${off ? 'opacity-40' : ''} ${p.end_mismatch ? 'bg-amber-50/70' : ''}`} data-testid="academy-preview-row">
                      <td className="p-2 font-semibold text-slate-700">
                        {p.name}
                        {p.existing_player_id && <span className="font-normal text-blue-500"> (موجود)</span>}
                        {p.duplicate_in_sheet && <span className="font-normal text-amber-600"> (مكرر مع {p.duplicate_in_sheet})</span>}
                      </td>
                      <td className="p-2 text-slate-600">{SUB_STATUS_LABELS[p.status] || p.status_raw || '—'}</td>
                      <td className="p-2 tabular-nums text-slate-700">
                        {nf(p.amount)}
                        {p.discount_value ? <span className="text-emerald-600"> (خصم {p.discount_value}{p.discount_type === 'percent' ? '%' : ''})</span> : null}
                      </td>
                      <td className="p-2 text-slate-500">{METHODS[p.payment_method] || p.payment_method_raw || '—'}{p.payment_date ? ` · ${p.payment_date}` : ''}</td>
                      <td className="p-2 text-slate-500" dir="ltr">
                        {p.subscription_start || '—'} ← {p.subscription_end || p.computed_end || '—'}
                        {p.end_mismatch && (
                          <span dir="rtl" className="mt-0.5 block text-[10px] font-bold text-amber-600" title={`الملف: ${p.subscription_end} — المحسوب من الفترة: ${p.computed_end}`}>
                            لا يطابق الفترة (المحسوب {p.computed_end})
                          </span>
                        )}
                      </td>
                      <td className="p-2 tabular-nums">
                        {p.days_left === null || p.days_left === undefined ? <span className="text-slate-400">—</span>
                          : p.days_left < 0 ? <span className="font-bold text-red-600">متأخر {Math.abs(p.days_left)} يوم</span>
                            : <span className="text-slate-600">متبقي {p.days_left} يوم</span>}
                        {p.arrears_days !== null && p.arrears_days !== undefined && (
                          <span className="mt-0.5 block text-[10px] text-slate-400">بالملف: {p.arrears_days}</span>
                        )}
                      </td>
                      <td className="p-2">
                        <button type="button" onClick={() => onToggle(p.key)} className="text-xs font-semibold text-slate-400 hover:text-red-500" data-testid={`academy-toggle-exclude-${p.key}`}>
                          {off ? 'تراجع' : 'استبعاد'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AcademyImportModal({ state, setState, categories, onConfirm, onClose }) {
  if (!state) return null;
  const preview = state.preview;
  const s = preview?.summary;

  const toggle = (key) => setState((st) => {
    const ex = new Set(st.excluded);
    if (ex.has(key)) ex.delete(key); else ex.add(key);
    return { ...st, excluded: ex };
  });

  const activeCount = preview
    ? preview.sheets.reduce((acc, sh) => acc + (sh.players || []).filter((p) => !state.excluded.has(p.key)).length, 0)
    : 0;

  return (
    <Modal open title="استيراد كشف استحقاق الأكاديمية" onClose={onClose} wide>
      {state.busy && !preview && <p className="text-sm text-slate-500">جارٍ قراءة الملف وتحليل الأوراق...</p>}
      {state.error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="academy-import-error">{state.error}</div>}

      {preview && (
        <div className="space-y-4" data-testid="academy-import-preview">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <SummaryCard testId="academy-summary-sheets" value={s.detected_sheets} label="أوراق مقروءة" tone="bg-slate-100 text-slate-700" />
            <SummaryCard testId="academy-summary-new" value={s.new_players} label="لاعبون جدد" tone="bg-teal-50 text-teal-700" />
            <SummaryCard testId="academy-summary-existing" value={s.existing_players} label="موجودون (سيُحدّثون)" tone="bg-blue-50 text-blue-700" />
            <SummaryCard testId="academy-summary-payments" value={s.payments_count} label="دفعات ستُسجَّل" tone="bg-emerald-50 text-emerald-700" />
            <SummaryCard testId="academy-summary-amount" value={nf(s.total_amount)} label="إجمالي المبالغ (ر.س)" tone="bg-amber-50 text-amber-700" />
          </div>

          <label className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5" data-testid="academy-record-payments-wrap">
            <input type="checkbox" checked={state.recordPayments} onChange={(e) => setState((st) => ({ ...st, recordPayments: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-teal-600" data-testid="academy-record-payments-toggle" />
            <span className="text-xs text-slate-600">
              <b className="text-slate-800">تسجيل المبالغ كدفعات مستلمة في المالية</b>
              <span className="mt-0.5 block">يُنشئ إيراداً بتاريخ السداد المذكور في الكشف لكل لاعب ويظهر في تقارير الإيرادات. عند إلغاء التحديد يُستورد الاشتراك فقط بلا أي قيد مالي.</span>
            </span>
          </label>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="academy-end-source-box">
            <div className="text-xs font-bold text-slate-800">مصدر «نهاية الاشتراك»</div>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              عمود «المتأخرات/المتبقي» في الكشف معادلة حيّة تساوي <b>نهاية الاشتراك ناقص تاريخ اليوم</b>، لذلك الرقم الموجب يعني أياماً متبقية والسالب يعني أياماً متأخرة. النظام لا يستورد هذا العمود، بل يحسب التأخير من تاريخ النهاية مباشرة.
            </p>
            <div className="mt-2 space-y-1.5">
              <label className="flex items-start gap-2">
                <input type="radio" name="academy-end-source" checked={state.endSource !== 'computed'} onChange={() => setState((st) => ({ ...st, endSource: 'file' }))} className="mt-0.5 accent-teal-600" data-testid="academy-end-source-file" />
                <span className="text-xs text-slate-600"><b>كما في الملف</b> — اعتمد عمود «نهاية الاشتراك» كما كتبته الأكاديمية</span>
              </label>
              <label className="flex items-start gap-2">
                <input type="radio" name="academy-end-source" checked={state.endSource === 'computed'} onChange={() => setState((st) => ({ ...st, endSource: 'computed' }))} className="mt-0.5 accent-teal-600" data-testid="academy-end-source-computed" />
                <span className="text-xs text-slate-600"><b>احسبها من تاريخ السداد + الفترة</b> — تصحّح الصفوف التي كُتب فيها تاريخ نهاية غير مطابق للفترة</span>
              </label>
            </div>
            {s.end_mismatches > 0 && (
              <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800" data-testid="academy-end-mismatch-warning">
                تنبيه: {s.end_mismatches} صفاً تاريخ النهاية فيه لا يطابق (تاريخ السداد + الفترة) — مُعلَّمة بالأصفر داخل الأوراق.
              </p>
            )}
            {s.overdue_count > 0 && (
              <p className="mt-1.5 text-[11px] font-semibold text-red-600" data-testid="academy-overdue-note">{s.overdue_count} لاعباً اشتراكه منتهٍ فعلاً حسب هذا الحساب.</p>
            )}
          </div>

          {preview.suggested_new_categories?.length > 0 && (
            <CategoryResolutionSection
              suggestions={preview.suggested_new_categories}
              resolutions={state.resolutions}
              onChange={(name, val) => setState((st) => ({ ...st, resolutions: { ...st.resolutions, [name]: val } }))}
              categories={categories}
              testidPrefix="academy"
            />
          )}

          <div className="space-y-2">
            {preview.sheets.map((sh) => (
              <SheetBlock key={sh.sheet} sheet={sh} excluded={state.excluded} onToggle={toggle} />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs text-slate-400">سيتم استيراد {activeCount} صفاً — الصفوف المستبعدة لن تُحفظ</span>
            <div className="flex gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600" data-testid="academy-cancel-btn">إلغاء</button>
              <PrimaryBtn onClick={onConfirm} disabled={state.busy || activeCount === 0} data-testid="academy-confirm-btn">
                {state.busy ? 'جارٍ الاستيراد...' : 'تأكيد الاستيراد'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
